const TOKEN_KEY = 'token';
const USER_KEY = 'user';
/** Marge avant expiration pour éviter les courses (secondes). */
const EXPIRY_SKEW_SECONDS = 30;

export type AuthTokenPayload = {
  user_id?: number;
  email?: string;
  roles?: string[];
  exp?: number;
  username?: string;
};

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  const token = localStorage.getItem(TOKEN_KEY);
  return token && token.trim() !== '' ? token : null;
}

export function parseAuthToken(token: string): AuthTokenPayload | null {
  try {
    const decoded = JSON.parse(atob(token));
    if (decoded && typeof decoded === 'object') {
      return decoded as AuthTokenPayload;
    }
  } catch {
    // Format JWT éventuel
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson);
    if (payload && typeof payload === 'object') {
      return payload as AuthTokenPayload;
    }
  } catch {
    return null;
  }

  return null;
}

export function getTokenExpiryMs(token: string): number | null {
  const payload = parseAuthToken(token);
  if (!payload?.exp || !Number.isFinite(payload.exp)) {
    return null;
  }
  return payload.exp * 1000;
}

export function isTokenExpired(token: string | null, skewSeconds = EXPIRY_SKEW_SECONDS): boolean {
  if (!token) return true;
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) return false;
  return expiryMs <= Date.now() + skewSeconds * 1000;
}

export function clearAuthSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  clearAuthSession();
  window.location.href = '/login';
}

export function hasValidAuthSession(): boolean {
  const token = getStoredToken();
  if (!token) return false;
  return !isTokenExpired(token);
}

export function getRolesFromSession(storedUser?: { roles?: string[] } | null): string[] {
  const token = getStoredToken();
  const tokenRoles = token ? parseAuthToken(token)?.roles : undefined;
  const userRoles = storedUser?.roles;
  const roles = Array.isArray(userRoles) && userRoles.length > 0 ? userRoles : tokenRoles;
  return Array.isArray(roles) ? roles : [];
}

export function isSuperAdminSession(storedUser?: { roles?: string[] } | null): boolean {
  return getRolesFromSession(storedUser).includes('ROLE_SUPER_ADMIN');
}
